import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../Context/LanguageContext';
import { isAuthenticated, loginWithPassword } from '../utils/cloudApi';
import HaechieSplitLogin, { ShieldMark } from './login/HaechieSplitLogin';
import './login/splitLogin.css';
import './login/loginFormBase.css';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.username || !formData.password) {
      setError(t('login.errorBothFields'));
      setLoading(false);
      return;
    }

    try {
      await loginWithPassword(formData.username.trim(), formData.password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || t('login.errorInvalid'));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <HaechieSplitLogin>
      <div className="login-header">
        <h2>
          <ShieldMark />
          {t('login.signIn')}
        </h2>
        <p>to a safer, smarter tomorrow</p>
      </div>

      <form onSubmit={handleSubmit} className="login-form" noValidate>
        <div className="input-group">
          <label htmlFor="username">{t('login.username')}</label>
          <input
            type="text"
            id="username"
                name="username"
            autoComplete="username"
            placeholder="Enter username"
                value={formData.username}
                onChange={handleChange}
            disabled={loading}
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="password">{t('login.password')}</label>
          <div className="password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
                name="password"
              autoComplete="current-password"
              placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
              disabled={loading}
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.62 21.62 0 0 1 5.06-6.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.62 21.62 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="form-options">
          <label className="remember-me">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>Remember me</span>
          </label>
        </div>

        {error ? <p className="login-error" role="alert">{error}</p> : null}

        <button type="submit" className="login-button" disabled={loading}>
                {loading ? t('login.signingIn') : t('login.signIn')}
          {!loading ? <span className="login-button-arrow" aria-hidden="true">→</span> : null}
        </button>
      </form>
    </HaechieSplitLogin>
  );
};

export default Login;
